import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, debounceTime, distinctUntilChanged, map, switchMap, take } from 'rxjs';

import { AuthRoleService } from 'app/services/auth-role.service';
import { IAuthRoleAttributes } from 'app/shared/interfaces/auth-role.interface';

interface RoleOption {
  value: string;
  code: string;
  label: string;
}

/**
 * Reusable select for RBAC roles. Mirrors the public API of
 * `CompanyMongoIdSelectComponent` (signal-input `control`, `multiple` flag).
 * Loads roles via `AuthRoleService.filter` with debounced search and
 * hydrates pre-selected values when the FormControl already has ids.
 */
@Component({
  selector: 'app-role-multi-select',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgxMatSelectSearchModule,
  ],
  template: `
    <mat-form-field appearance="fill" class="w-full">
      <mat-label>{{ label() }}</mat-label>
      <mat-select [formControl]="control()" [multiple]="multiple()">
        <mat-select-trigger>{{ selectedLabelsText() }}</mat-select-trigger>
        <mat-option>
          <ngx-mat-select-search
            [formControl]="searchControl"
            placeholderLabel="Buscar rol..."
            noEntriesFoundLabel="No se encontraron roles"
            [searching]="isSearchLoading()"
          />
        </mat-option>
        @if (isSearchLoading()) {
          <mat-option disabled>
            <div class="flex items-center gap-2">
              <mat-spinner diameter="20"></mat-spinner>
              <span>Cargando...</span>
            </div>
          </mat-option>
        } @else {
          @for (option of sortedOptions(); track option.value) {
            <mat-option [value]="option.value" class="leading-none">
              <div class="font-bold text-xs">{{ option.code }}</div>
              <div class="text-sm mt-0.5">{{ option.label }}</div>
            </mat-option>
          }
        }
      </mat-select>
      @if (isSearchLoading()) {
        <mat-spinner matSuffix diameter="16"></mat-spinner>
      } @else if (hasValue()) {
        <button matSuffix mat-icon-button aria-label="Limpiar" (click)="clear($event)">
          <mat-icon>close</mat-icon>
        </button>
      }
    </mat-form-field>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleMultiSelectComponent implements OnInit {
  private readonly authRoleService = inject(AuthRoleService);
  private readonly destroyRef = inject(DestroyRef);

  control = input.required<FormControl>();
  multiple = input(false);
  label = input<string>('Rol');

  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  readonly isSearchLoading = signal(false);
  private readonly allOptions = signal<RoleOption[]>([]);
  private readonly selectedOptions = signal<RoleOption[]>([]);
  private readonly currentValue = signal<string | string[] | null>(null);

  readonly sortedOptions = computed(() => {
    const selected = this.selectedOptions();
    const all = this.allOptions();
    const selectedIds = new Set(selected.map((s) => s.value));
    return [...selected, ...all.filter((o) => !selectedIds.has(o.value))];
  });

  readonly selectedLabelsText = computed(() => {
    const v = this.currentValue();
    const all = [...this.selectedOptions(), ...this.allOptions()];
    const labelMap = new Map(all.map((o) => [o.value, o.label]));
    if (Array.isArray(v)) {
      return v.map((id) => labelMap.get(id) ?? id).join(', ');
    }
    return v ? (labelMap.get(v) ?? v) : '';
  });

  readonly hasValue = computed(() => {
    const v = this.currentValue();
    return Array.isArray(v) ? v.length > 0 : v != null;
  });

  private readonly searchSubject = new Subject<string>();

  ngOnInit(): void {
    const formControl = this.control();
    this.currentValue.set(formControl.value);

    const initialIds = this.extractIds(formControl.value);
    if (initialIds.length > 0) {
      this.loadSelectedRoles(initialIds);
    }

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isSearchLoading.set(true);
          return this.searchRoles(term);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((options) => {
        this.allOptions.set(options);
        this.isSearchLoading.set(false);
      });

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.searchSubject.next(term ?? ''));

    // Initial load (first 25 active roles)
    this.searchSubject.next('');

    formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        this.currentValue.set(v);
        this.syncSelectedOptions(v);
      });
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.control().reset();
  }

  private extractIds(v: string | string[] | null): string[] {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v) return [v];
    return [];
  }

  private syncSelectedOptions(v: string | string[] | null): void {
    const ids = this.extractIds(v);
    if (ids.length === 0) {
      this.selectedOptions.set([]);
      return;
    }
    const known = new Map(
      [...this.selectedOptions(), ...this.allOptions()].map((o) => [o.value, o]),
    );
    const resolved = ids
      .map((id) => known.get(id))
      .filter((o): o is RoleOption => !!o);
    this.selectedOptions.set(resolved);
  }

  private searchRoles(term: string) {
    const filter: Record<string, unknown> = { statusId: 1 };
    if (term && term.trim().length > 0) {
      filter['$or'] = [
        { name: { $regex: term, $options: 'i' } },
        { code: { $regex: term, $options: 'i' } },
      ];
    }
    return this.authRoleService.filter(filter).pipe(
      map((res) => (res?.data ?? []).slice(0, 25).map((r) => this.toOption(r))),
    );
  }

  private loadSelectedRoles(ids: string[]): void {
    if (!ids.length) return;
    this.authRoleService
      .filter({ _id: { $in: ids } })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.selectedOptions.set((res?.data ?? []).map((r) => this.toOption(r)));
        },
        error: () => this.selectedOptions.set([]),
      });
  }

  private toOption(r: IAuthRoleAttributes): RoleOption {
    return {
      value: r._id,
      code: r.code ?? '',
      label: r.name ?? '',
    };
  }
}
